/**
 * ClinicianDashboard — single-page clinician workflow.
 *
 * Replaces the legacy /dashboard/patientdashboard shell for clinicians:
 *   - Top bar with logo, hospital, site, profile/settings/logout actions
 *   - Left pane: search + sort + scrollable patient list
 *   - Right pane: in-place view of symptoms, messages, profile, or settings
 *   - Report dialog (date-range picker) — phase 1 shows the dialog UI only
 *
 * No internal routing. State lives in this component; clicking a patient
 * card sub-element (symptoms tile, messages tile, report button) flips
 * the right-pane content. Profile/Settings/Logout in the top bar do the
 * same — Profile and Settings render the existing components inline.
 */
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { IonImg, IonSpinner } from "@ionic/react";
import { FiMessageSquare, FiUser, FiSettings, FiLogOut, FiSearch, FiFileText, FiActivity } from "react-icons/fi";
import { IoMdPerson } from "react-icons/io";
import moment from "moment";
import { BehaviorSubject } from "rxjs";
import * as _ from "lodash";

// PDF generation — same stack as datasync.jsx
import { generate } from "@pdfme/generator";
import { text, image } from "@pdfme/schemas";
import { pdftemplate, IMPLANT_DATE_OF_VISIT } from "../../util/config";

import calyanlogo from "../../assets/logos/calyanLogo.svg";
import AppContext from "../../components/AppContext";
import { useAppData } from "../../context/appContext";
import { getUserInfo, handleLogout } from "../../service/adminUI";
import SessionManager from "../../service/SessionManager";
import ErrorLoggingService from "../../service/ErrorLoggingService";
import { getHistoricalData } from "../../util/helpers/HistoricalData";
import { unescapeCRFData } from "../../util/helpers/helperfunctions";
import { renderReportCharts, REPORT_SPAN_WEEKS } from "../../util/reportCharts";
import { formatAppDate, formatAppDateTime } from "../../utils/dateFormat";

import Profile from "../profile/Profile";
import Settings from "../settings/Settings";

import "./clinicianDashboard.css";

// Historical-fetch tuning — mirrors datasync.jsx (line 46-51).
// Stream can go quiet between chunks; treat as ended only after long idle.
const REPORT_SSE_INACTIVITY_MS = 120000; // 2 min
const REPORT_SSE_MAX_MS = 1800000;       // 30 min safety cap
// Features carrying data we care about for the aggregated report.
const REPORT_RELEVANT_FEATURES = new Set([
  "deviceparameters", "devicestatus", "patientinfo",
  "batteryvoltage", "batteryparameter", "leadimpedance", "leadimpedanceweeklymin",
  "pacehistogram", "sensehistogram",
  "rwaveamplitudeweeklymin", "batteryvoltagetrendmin",
  // Paced/sensed split — sampled per revision to build the markers plot.
  "ventricularmarkers",
]);

// ── Thresholds (red-flag boundaries) ────────────────────────────────
const RECHARGE_SOON_DAYS = 30;          // days remaining before recharge is due
const IMPEDANCE_MIN = 200;              // ohms
const IMPEDANCE_MAX = 2000;             // ohms
const STALE_DAYS = 30;                  // days

// Report ceiling for lead impedance. A reading above this is an open or
// shorted lead rather than a measurement, so the report says ">1200 Ω"
// instead of printing a number that implies precision it doesn't have.
// Matches RANGE.impedance.max in util/reportCharts.js.
const IMPEDANCE_CEILING = 1200;         // ohms

// ── Right-pane content modes ────────────────────────────────────────
// Profile/Settings are NOT here — they aren't patient-context views.
// Profile opens as a modal overlay; Settings takes over the full area
// below the topbar. See `profileModalOpen` state + the SETTINGS view at
// the top-level render branch.
const VIEW = {
  NONE: "none",
  SYMPTOMS: "symptoms",
  MESSAGES: "messages",
  REPORT: "report",
  SETTINGS: "settings",
};

// ── Sort options ────────────────────────────────────────────────────
const SORT_OPTIONS = [
  { value: "patientId",   label: "Patient ID (A→Z)" },
  { value: "recharge",    label: "Days to recharge (soonest first)" },
  { value: "lastSync",    label: "Time since last sync (longest first)" },
  { value: "impedance",   label: "Impedance (highest first)" },
];

// ── Helpers ─────────────────────────────────────────────────────────
const daysAgo = (iso) => {
  if (!iso) return null;
  const m = moment(iso);
  if (!m.isValid()) return null;
  return moment().diff(m, "days");
};

// Build the pdftemplate inputs array from an *aggregated* device thing
// (features merged across all SSE revisions in the picked date range),
// plus eCRF subject/implant data. Mirrors the field mapping in
// datasync.jsx handlePreview but drops the per-session (`selectedDatasynclog`)
// branch — we're working with a virtual "latest state in window" device.
// EGM graphs are intentionally left empty; the report is text + trends,
// and the trend graphs (impedance, R-wave, histograms) land in phase 2.
const buildReportPdfInputs = ({ device, subjectData, implantData, rangeFrom, rangeTo, charts, windowNote }) => {
  const feats = device?.features || {};
  const attrs = device?.attributes || {};

  // Impedance beside the FLEXARM heading. The charts module already
  // applies the ceiling rule (>1200 Ω), so prefer its headline; fall back
  // to the spot reading in leadimpedance only when the trend is empty.
  const currentImpedance = (() => {
    if (charts?.headline?.impedance) return charts.headline.impedance;
    const s = feats?.leadimpedance?.properties?.impedanceohms;
    const n = s == null ? NaN : parseFloat(s);
    if (!Number.isFinite(n) || n <= 0) return "";
    return n > IMPEDANCE_CEILING ? `>${IMPEDANCE_CEILING} Ω` : `${Math.round(n)} Ω`;
  })();

  // Days until the device must be recharged, shown beside the battery
  // plot rather than in a separate block (the plot gives it context).
  const daysToRecharge = (() => {
    const props = feats?.batteryparameter?.properties;
    const v = props?.["Days to RRI"] ?? props?.["Battery Life Details"]?.["Days to RRI"];
    const n = v == null ? NaN : Number(v);
    return Number.isFinite(n) ? `Days to Required Recharge: ${Math.max(0, Math.round(n))}` : "";
  })();

  // Separator stays ASCII — the font pdfme embeds has no U+2192 glyph, so an
  // arrow renders as a tofu box in the generated PDF.
  const rangeLabel =
    formatAppDate(rangeFrom) + " to " + formatAppDate(rangeTo);

  const dob = (() => {
    if (!_.isEmpty(subjectData)) return formatAppDate(subjectData.attributes?.dob);
    return formatAppDate(feats?.patientinfo?.properties?.dob);
  })();

  const implantDate = (() => {
    if (implantData && implantData[IMPLANT_DATE_OF_VISIT]?.value) {
      return formatAppDate(implantData[IMPLANT_DATE_OF_VISIT].value);
    }
    return (
      formatAppDate(feats?.patientinfo?.properties?.implantdate) ||
      formatAppDate(attrs?.mfgdate)
    );
  })();

  // Model number and device type are separate facts: the model is the
  // hardware part number, the type is the pacing configuration. They used
  // to be concatenated into "device type", which left Model Number showing
  // the raw attribute and Type of Device showing both.
  const model = attrs?.model || feats?.patientinfo?.properties?.modelnumber || "";
  const deviceType = attrs?.technology || "";
  const serialNo = feats?.patientinfo?.properties?.serialnumber || attrs?.serialnumber || "";
  const pacingMode = feats?.deviceparameters?.properties?.pacingmode || "";

  return [{
    "PatientId": attrs?.subjectId || "",
    "patient id": attrs?.subjectId || "",
    "DOB": dob,
    "model": model,
    "device type": deviceType,
    "implant date": implantDate,
    "serial no": serialNo,
    "Pacing mode": pacingMode,
    "Current impedance": currentImpedance,
    "rwave latest": charts?.headline?.rwave || "",
    "days to recharge": daysToRecharge,
    "interrogation date": rangeLabel,
    // Empty unless the window held nothing chartable, in which case it says
    // so on the face of the report rather than leaving a reader to wonder
    // why every plot is blank.
    "window note": windowNote || "",
    // Chart images (base64 PNG data URLs). A series with no samples still
    // renders a framed panel carrying a "no data" notice, so the layout is
    // identical for every patient.
    "rwave trend graph": charts?.images?.rwave || "",
    "flexarm impedance graph": charts?.images?.leadImpedance || "",
    "battery trend graph": charts?.images?.battery || "",
    "ventricular markers graph": charts?.images?.markers || "",
    "ventricular histogram graph": charts?.images?.histogram || "",
  }];
};

// Days remaining before the device must be recharged.
//
// The device reports this as "Days to RRI" (Recommended Replacement
// Indicator) — the countdown the firmware computes from the current SOC,
// pacing ratio, rate, amplitude, pulse width and lead impedance. It is
// published both at the top level of `batteryparameter` and again inside
// the "Battery Life Details" sub-object; read the top-level copy first
// and fall back to the nested one.
const extractDaysToRecharge = (device) => {
  const props = device?.features?.batteryparameter?.properties;
  const v = props?.["Days to RRI"] ?? props?.["Battery Life Details"]?.["Days to RRI"];
  if (v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? Math.max(0, Math.round(n)) : null;
};

const extractImpedance = (device) => {
  const v = device?.features?.leadimpedance?.properties?.impedanceohms;
  if (v != null) {
    const n = parseFloat(v);
    if (Number.isFinite(n) && n > 0) return Math.round(n);
  }
  // Fallback: first non-NaN of leadimpedanceweeklymin
  const arr = device?.features?.leadimpedanceweeklymin?.properties?.impedancevaluesohms;
  if (Array.isArray(arr)) {
    for (const x of arr) {
      const n = parseFloat(x);
      if (Number.isFinite(n) && n > 0) return Math.round(n);
    }
  }
  return null;
};


const ClinicianDashboard = () => {
  const history = useHistory();
  const appContextData = useAppData();
  const { userData, appData, refreshTimerId, setAppData, setRefreshTimerId, dtalService } = useContext(AppContext);

  // ── State ────────────────────────────────────────────────────────
  const [patients, setPatients] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [userInfo, setUserInfo] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("patientId");
  const [selectedPatientId, setSelectedPatientId] = useState(null);
  const [view, setView] = useState(VIEW.NONE);

  // Right-pane data caches (loaded on click)
  const [symptomList, setSymptomList] = useState([]);
  const [messageList, setMessageList] = useState([]);
  const [rightPaneLoading, setRightPaneLoading] = useState(false);

  // Tokens for canceling in-flight loads when the user switches patients
  // mid-fetch. Each loadSymptoms/loadMessages call captures the current
  // value of the ref; if the ref has moved on by completion time, the
  // resulting array is discarded.
  const symptomLoadRef = useRef(0);
  const messageLoadRef = useRef(0);

  // Profile modal (user-context — overlays the dashboard)
  const [profileModalOpen, setProfileModalOpen] = useState(false);

  // There is no date-range picker: the window is always the fixed 78-week
  // span (see handleReportClick), so clicking Report goes straight to
  // generation — historical SSE fetch → merge features across revisions →
  // build pdftemplate inputs → generate PDF → preview in the right pane.

  // Inline report generation (right-pane VIEW.REPORT).
  // phase: 'loading' (fetching historical stream) → 'generating' (building PDF)
  //        → 'ready' (blob URL populated) → 'error' (error message set)
  // A window with no data is not a failure state: the report is generated
  // either way, with empty plots and a note saying so.
  const [reportPhase, setReportPhase] = useState(null);
  const [reportError, setReportError] = useState("");
  const [reportProgress, setReportProgress] = useState("");
  const [reportBlobUrl, setReportBlobUrl] = useState("");
  // Set when the window held nothing chartable; shown above the preview and
  // printed into the report itself.
  const [reportNotice, setReportNotice] = useState("");
  const [reportRange, setReportRange] = useState(null);
  const [reportForSubjectId, setReportForSubjectId] = useState(null);
  const [reportRevisionsCount, setReportRevisionsCount] = useState(0);

  // Cancellation ref — set to true when a new report starts or when the
  // right-pane view moves away, so an in-flight fetch discards its results
  // instead of applying them stale.
  const reportRunRef = useRef(0);

  // Toast (lightweight feedback)
  const [toast, setToast] = useState(null);
  const showToast = (msg, kind = "info") => {
    setToast({ msg, kind });
    setTimeout(() => setToast(null), 3500);
  };

  // ── Fetch user info (hospital, name, etc.) ───────────────────────
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const token = SessionManager.getAccessToken();
        const info = await getUserInfo(token);
        if (mounted) setUserInfo(info);
      } catch (err) {
        ErrorLoggingService.warn("Failed to load user info", err);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // ── Fetch patients ───────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    const fetchPatients = async () => {
      setIsLoading(true);
      try {
        const devices = await dtalService.fetch("pacemaker", undefined, "thingId,attributes,features,_modified");
        let filtered = (devices?.items || []).filter((d) => d?.attributes);

        // Site-based filter via eCRF_subjects (mirrors dashboard.jsx pattern)
        if (userData.activeSiteId) {
          const siteFilter = `eq(attributes/siteId,"${userData.activeSiteId}")`;
          const siteSubjects = await dtalService.fetch("eCRF_subjects", siteFilter, "attributes/patientId");
          const allowedIds = new Set(
            (siteSubjects?.items || [])
              .map((s) => s?.attributes?.patientId)
              .filter(Boolean)
          );
          filtered = filtered.filter((d) => allowedIds.has(d?.attributes?.subjectId));
        }

        if (cancelled) return;
        setPatients(filtered);
        appContextData.setAllPatientData(filtered);
      } catch (err) {
        ErrorLoggingService.error("ClinicianDashboard: fetchPatients failed", err);
        if (!cancelled) setPatients([]);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    if (userData.userType === "clinician") {
      fetchPatients();
    } else {
      setIsLoading(false);
    }

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData.userType, userData.activeSiteId]);

  // ── Derive sorted / filtered patient list ────────────────────────
  const sortedPatients = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let arr = patients.filter((d) => {
      if (!q) return true;
      const sid = (d?.attributes?.subjectId || "").toLowerCase();
      return sid.includes(q);
    });

    const getMetric = (d) => {
      switch (sortBy) {
        case "patientId":   return (d?.attributes?.subjectId || "").toLowerCase();
        case "recharge":    return extractDaysToRecharge(d) ?? Number.MAX_SAFE_INTEGER;
        case "lastSync":    return -(daysAgo(d?._modified) ?? -1);
        // Comparator below sorts ascending, so "highest first" negates the
        // value. Devices with no impedance reading sort to the bottom.
        case "impedance": {
          const ohms = extractImpedance(d);
          return ohms == null ? Number.MAX_SAFE_INTEGER : -ohms;
        }
        default:            return 0;
      }
    };

    return arr.slice().sort((a, b) => {
      const ma = getMetric(a);
      const mb = getMetric(b);
      if (typeof ma === "string" && typeof mb === "string") return ma.localeCompare(mb);
      return (ma < mb ? -1 : ma > mb ? 1 : 0);
    });
  }, [patients, searchQuery, sortBy]);

  // ── Patient selection ───────────────────────────────────────────
  const selectedPatient = useMemo(
    () => patients.find((d) => d?.attributes?.subjectId === selectedPatientId) || null,
    [patients, selectedPatientId]
  );

  // ── Right-pane content loaders ──────────────────────────────────
  // Both loaders use getHistoricalData (the SSE-based helper used by the
  // existing /messages and /patientlog pages). dtalService.get returns
  // only the CURRENT state of a Ditto thing — it can't show historical
  // revisions, which is how messages + symptoms accumulate over time.
  //
  // Each call captures a token from the matching ref; if the ref has
  // moved on by the time the SSE finishes, we silently discard the
  // result (user clicked a different patient or view).

  const loadSymptoms = async (subjectId) => {
    const myToken = ++symptomLoadRef.current;
    setRightPaneLoading(true);
    setSymptomList([]);

    const symptomsMap = new Map();
    const ctrl = new BehaviorSubject(false);
    const streamKey = "clin-symptoms";

    let ctrlSub = null;
    let sseSub = null;

    const finalize = () => {
      if (ctrlSub) { ctrlSub.unsubscribe(); ctrlSub = null; }
      if (sseSub && typeof sseSub.unsubscribe === "function") { sseSub.unsubscribe(); sseSub = null; }
      dtalService.unsubscribe(streamKey).catch(() => {});
      if (myToken !== symptomLoadRef.current) return; // user moved on

      const items = Array.from(symptomsMap.values())
        .map((t) => {
          const props = t?.features?.content?.properties || {};
          const ts = t?._modified || props?.timeOfOccurence || null;
          const name = props?.name || "";
          const body = props?.message || props?.description || "";
          return { ts, name, body };
        })
        .filter((s) => s.name || s.body)
        .sort((a, b) => moment(b.ts).valueOf() - moment(a.ts).valueOf());

      setSymptomList(items);
      setRightPaneLoading(false);
    };

    try {
      ctrlSub = ctrl.subscribe((done) => { if (done) finalize(); });
      sseSub = await getHistoricalData({
        dtalService,
        identifier: `symptom:${subjectId}`,
        thingUpdates: symptomsMap,
        duControlSubject: ctrl,
        streamKey,
        inactivityPeriod: 3000,
      });
      // getHistoricalData signals completion via ctrl.next(true) — if
      // it returned null (no stream), it already did so synchronously.
      if (!sseSub) finalize();
    } catch (err) {
      ErrorLoggingService.warn("loadSymptoms failed", err);
      finalize();
    }
  };

  const loadMessages = async (subjectId) => {
    const myToken = ++messageLoadRef.current;
    setRightPaneLoading(true);
    setMessageList([]);

    // Two channels: clinician→patient and patient→clinician.
    const cToPMap = new Map();    // identifier: message:<sid>
    const pToCMap = new Map();    // identifier: message:<sid>-patient
    const ctrlCToP = new BehaviorSubject(false);
    const ctrlPToC = new BehaviorSubject(false);
    let cToPDone = false;
    let pToCDone = false;

    let subCToPCtrl = null, subPToCCtrl = null;
    let sseCToP = null, sseToP = null;

    const finalize = () => {
      if (subCToPCtrl) { subCToPCtrl.unsubscribe(); subCToPCtrl = null; }
      if (subPToCCtrl) { subPToCCtrl.unsubscribe(); subPToCCtrl = null; }
      if (sseCToP && typeof sseCToP.unsubscribe === "function") { sseCToP.unsubscribe(); sseCToP = null; }
      if (sseToP && typeof sseToP.unsubscribe === "function") { sseToP.unsubscribe(); sseToP = null; }
      dtalService.unsubscribe("clin-msg-out").catch(() => {});
      dtalService.unsubscribe("clin-msg-in").catch(() => {});
      if (myToken !== messageLoadRef.current) return; // user moved on

      const flatten = (map, defaultDirection) =>
        Array.from(map.values()).map((t) => {
          const props = t?.features?.content?.properties || {};
          const sender = props?.senderId || "";
          const direction =
            sender === userData.userId ? "out"
            : sender ? "in"
            : defaultDirection;
          return {
            ts: t?._modified || null,
            body: props?.plaintext || props?.text || "(no content)",
            direction,
            sender,
          };
        });

      // Deduplicate by (timestamp, body) — same message can land on both
      // channels under certain server-side fan-out edge cases.
      const seen = new Map();
      [...flatten(cToPMap, "out"), ...flatten(pToCMap, "in")].forEach((m) => {
        const key = `${m.ts}|${m.body}`;
        if (!seen.has(key)) seen.set(key, m);
      });
      const merged = Array.from(seen.values())
        .sort((a, b) => moment(b.ts).valueOf() - moment(a.ts).valueOf());

      setMessageList(merged);
      setRightPaneLoading(false);
    };

    const maybeFinalize = () => { if (cToPDone && pToCDone) finalize(); };

    try {
      subCToPCtrl = ctrlCToP.subscribe((d) => { if (d) { cToPDone = true; maybeFinalize(); } });
      subPToCCtrl = ctrlPToC.subscribe((d) => { if (d) { pToCDone = true; maybeFinalize(); } });

      sseCToP = await getHistoricalData({
        dtalService,
        identifier: `message:${subjectId}`,
        thingUpdates: cToPMap,
        duControlSubject: ctrlCToP,
        streamKey: "clin-msg-out",
        inactivityPeriod: 3000,
      });
      if (!sseCToP) { cToPDone = true; maybeFinalize(); }

      sseToP = await getHistoricalData({
        dtalService,
        identifier: `message:${subjectId}-patient`,
        thingUpdates: pToCMap,
        duControlSubject: ctrlPToC,
        streamKey: "clin-msg-in",
        inactivityPeriod: 3000,
      });
      if (!sseToP) { pToCDone = true; maybeFinalize(); }
    } catch (err) {
      ErrorLoggingService.warn("loadMessages failed", err);
      finalize();
    }
  };

  const handleSelectPatient = (subjectId) => {
    setSelectedPatientId(subjectId);
    // Default right-pane view: clear back to none when changing patient.
    if (view !== VIEW.PROFILE && view !== VIEW.SETTINGS) setView(VIEW.NONE);
  };

  const handleSymptomsClick = (subjectId, e) => {
    e?.stopPropagation();
    setSelectedPatientId(subjectId);
    setView(VIEW.SYMPTOMS);
    loadSymptoms(subjectId);
  };

  const handleMessagesClick = (subjectId, e) => {
    e?.stopPropagation();
    setSelectedPatientId(subjectId);
    setView(VIEW.MESSAGES);
    loadMessages(subjectId);
  };

  // The report always covers a fixed 78-week window ending today, so there
  // is nothing to ask: clicking Report generates it. A fixed span means two
  // reports for the same patient — or for two different patients — can be
  // laid side by side and compared directly, and it guarantees a full year
  // of history is present at an annual follow-up. The plots carry the same
  // time axis whatever the data covers, so a shorter or patchier history
  // shows up as gaps rather than as a differently-scaled chart.
  const handleReportClick = (subjectId, e) => {
    e?.stopPropagation();
    setSelectedPatientId(subjectId);
    const to = moment();
    const from = to.clone().subtract(REPORT_SPAN_WEEKS, "weeks");
    generateReport(subjectId, from, to);
  };

  // ── Inline report generation ──────────────────────────────────────
  // Full pipeline: historical SSE fetch → merge revisions → build PDF
  // inputs → @pdfme generate → blob URL → preview iframe. Mirrors
  // datasync.jsx's SSE call signature exactly so the same server-side
  // path is exercised; the aggregation step is new and specific to
  // range-based reports.
  const generateReport = async (subjectId, fromMoment, toMoment) => {
    const runId = ++reportRunRef.current;
    setView(VIEW.REPORT);
    setReportForSubjectId(subjectId);
    // Display-only — the header strip and the empty-state message.
    setReportRange({
      from: formatAppDate(fromMoment),
      to:   formatAppDate(toMoment),
    });
    // Release any prior blob URL so we don't leak object URLs across runs.
    if (reportBlobUrl) {
      try { URL.revokeObjectURL(reportBlobUrl); } catch (_e) { /* noop */ }
      setReportBlobUrl("");
    }
    setReportPhase("loading");
    setReportError("");
    setReportNotice("");
    setReportProgress("Fetching data sync logs from the device…");
    setReportRevisionsCount(0);

    // Locate the target device thing from the loaded patient list.
    const device = patients.find((d) => d?.attributes?.subjectId === subjectId);
    if (!device) {
      setReportPhase("error");
      setReportError(`Device not found for ${subjectId}.`);
      return;
    }

    // eCRF data — same lookups datasync.jsx does before generating the PDF.
    let subjectDataAggr = null;
    let implantDataAggr = null;
    try {
      const [eCRFSubjectData, eCRFImplantData] = await Promise.all([
        dtalService.get("eCRF_subjects:" + subjectId).catch(() => []),
        dtalService.get("eCRF_implant:" + subjectId).catch(() => []),
      ]);
      subjectDataAggr = Array.isArray(eCRFSubjectData) ? eCRFSubjectData[0] : eCRFSubjectData;
      const implantThing = Array.isArray(eCRFImplantData) ? eCRFImplantData[0] : eCRFImplantData;
      implantDataAggr = implantThing?.features?.implant?.properties?.values;
      if (!_.isEmpty(implantDataAggr)) implantDataAggr = unescapeCRFData(implantDataAggr);
    } catch (err) {
      ErrorLoggingService.warn("Report: eCRF fetch failed (continuing)", err);
    }
    if (reportRunRef.current !== runId) return; // superseded

    // Historical SSE — identical call to datasync.jsx line 679-683.
    const sseFields = "thingId,features,_revision,_modified";
    const streamKey = "clinician-report";
    const fromIso = fromMoment.clone().startOf("day").toISOString();
    const toIso   = toMoment.clone().endOf("day").toISOString();

    // Feature merge accumulator. Keyed by feature name; each revision that
    // carries a feature overwrites the previous value (last-write-wins).
    // This reconstructs the device's "as-of end-of-range" state from the
    // stream, using only revisions inside the window.
    const mergedFeatures = {};
    // Ventricular paced/sensed split is a single pair per revision, not a
    // cumulative array, so last-write-wins would collapse it to one point.
    // Keep every sample and let the chart bucket them onto the time axis.
    const markerSeries = [];
    let lastModified = null;
    // Two counters, because they answer different questions. `revisionsSeen`
    // is "did this device sync at all in the window"; `usableRevisions` is
    // "did any of those logs carry something the report can chart". Folding
    // them together used to make a device that synced but reported nothing
    // chartable look like a device that had never synced.
    let revisionsSeen = 0;
    let usableRevisions = 0;
    let batchCounter = 0;

    const mergeResponse = (response) => {
      if (!response || !response.features) return;
      revisionsSeen += 1;
      const keys = Object.keys(response.features);
      const relevant = keys.some((k) => REPORT_RELEVANT_FEATURES.has(k));
      if (!relevant) return;
      for (const k of keys) {
        // Store the WHOLE feature object; last write wins per feature.
        // Cumulative-array features (histograms, trend arrays) naturally
        // benefit from picking the newest revision, which already carries
        // the fullest history in its .properties arrays.
        mergedFeatures[k] = response.features[k];
      }
      const vm = response.features.ventricularmarkers?.properties;
      if (vm && (vm.vspercentage != null || vm.vppercentage != null)) {
        markerSeries.push({
          ts: response._modified,
          vs: vm.vspercentage,
          vp: vm.vppercentage,
        });
      }
      if (response._modified) {
        if (!lastModified || String(response._modified) > String(lastModified)) {
          lastModified = response._modified;
        }
      }
      usableRevisions += 1;
    };

    let subscription = null;
    let inactivityTimer = null;
    let safetyTimer = null;
    let isDone = false;

    const finalize = async () => {
      if (isDone) return;
      isDone = true;
      if (inactivityTimer) clearTimeout(inactivityTimer);
      if (safetyTimer) clearTimeout(safetyTimer);
      if (subscription) { try { subscription.unsubscribe(); } catch (_e) {} }
      try { dtalService.unsubscribe(streamKey); } catch (_e) {}
      if (reportRunRef.current !== runId) return; // superseded

      setReportRevisionsCount(usableRevisions);

      // An empty window is not a failure. The report still generates, with
      // the device's identity filled in and every plot showing its "no data"
      // panel, and says why it is empty — the clinician gets a filed record
      // that the window was checked rather than a dead end.
      //
      // `mergedFeatures` deliberately stays empty in that case: the thing's
      // current features are out-of-window by definition, and printing them
      // under an in-window heading would misrepresent when they were read.
      const windowNote = (() => {
        if (usableRevisions > 0) return "";
        const lastSync = formatAppDate(device?._modified);
        const tail = lastSync ? ` Last sync ${lastSync}.` : "";
        if (revisionsSeen === 0) {
          return `No device data recorded in this ${REPORT_SPAN_WEEKS}-week window.${tail}`;
        }
        return `Device synced ${revisionsSeen} time${revisionsSeen === 1 ? "" : "s"} in this ` +
               `${REPORT_SPAN_WEEKS}-week window, but no trend data was recorded.${tail}`;
      })();
      setReportNotice(windowNote);

      // Build a virtual "aggregated device thing" that pdftemplate inputs
      // can read from — same shape as selectedDeviceData in datasync.jsx.
      const aggregatedDevice = {
        thingId: device.thingId,
        attributes: device.attributes,
        features: mergedFeatures,
        _modified: lastModified,
      };

      setReportPhase("generating");
      setReportProgress("Rendering trend charts…");
      // Off-screen Chart.js render → base64 PNGs for the image slots, plus
      // the headline values shown beside the plot titles. Failures degrade
      // to empty strings so the PDF still generates.
      let charts = { images: {}, headline: {} };
      try {
        charts = await renderReportCharts(mergedFeatures, {
          markerSeries,
          endMs: toMoment.valueOf(),
        });
      } catch (err) {
        ErrorLoggingService.warn("Report: chart rendering failed (continuing)", err);
      }
      if (reportRunRef.current !== runId) return; // superseded

      setReportProgress("Building PDF…");
      try {
        const inputs = buildReportPdfInputs({
          device: aggregatedDevice,
          subjectData: subjectDataAggr,
          implantData: implantDataAggr,
          rangeFrom: fromMoment,
          rangeTo: toMoment,
          charts,
          windowNote,
        });
        const pdf = await generate({ template: pdftemplate, inputs, plugins: { text, image } });
        if (reportRunRef.current !== runId) return; // superseded
        const blob = new Blob([pdf.buffer], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);
        setReportBlobUrl(url);
        setReportPhase("ready");
        setReportProgress("");
      } catch (err) {
        ErrorLoggingService.warn("Report: PDF generation failed", err);
        setReportPhase("error");
        setReportError("PDF generation failed. See console for details.");
      }
    };

    try {
      const sseSubject = await dtalService.gethistorical(
        device.thingId, null, null, fromIso, toIso, sseFields, streamKey
      );
      if (!sseSubject) { finalize(); return; }
      subscription = sseSubject.subscribe({
        next: (response) => {
          if (reportRunRef.current !== runId) return; // superseded
          mergeResponse(response);
          batchCounter += 1;
          if ((batchCounter % 10) === 0) {
            setReportProgress(`Collected ${revisionsSeen} data sync log${revisionsSeen === 1 ? "" : "s"} so far…`);
          }
          // Reset inactivity timer on each new event
          if (inactivityTimer) clearTimeout(inactivityTimer);
          inactivityTimer = setTimeout(finalize, REPORT_SSE_INACTIVITY_MS);
        },
        error: (_err) => { finalize(); },
        complete: () => { finalize(); },
      });
      // Fallback timers — Ditto's SSE quiets between chunks; treat as done
      // after long idle, and enforce a hard safety cap.
      inactivityTimer = setTimeout(finalize, REPORT_SSE_INACTIVITY_MS);
      safetyTimer = setTimeout(finalize, REPORT_SSE_MAX_MS);
    } catch (err) {
      ErrorLoggingService.warn("Report: gethistorical failed", err);
      setReportPhase("error");
      setReportError("Could not fetch data sync logs from the device.");
    }
  };

  // Cancel any in-flight report fetch when the right pane moves away.
  useEffect(() => {
    if (view !== VIEW.REPORT) {
      reportRunRef.current += 1; // supersede
    }
  }, [view]);

  // Release the blob URL on unmount to avoid a leak between mounts.
  useEffect(() => {
    return () => {
      if (reportBlobUrl) {
        try { URL.revokeObjectURL(reportBlobUrl); } catch (_e) {}
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Top bar actions ─────────────────────────────────────────────
  // Profile = modal overlay (user-context, short-lived). Settings =
  // full-screen takeover (more room for sections; clearer separation
  // from patient workspace).
  const handleProfileClick = () => { setProfileModalOpen(true); };
  const handleSettingsClick = () => { setView(VIEW.SETTINGS); };
  const handleSettingsBack = () => { setView(VIEW.NONE); };
  const handleLogoutClick = async () => {
    try {
      const result = await handleLogout(userData.userId, appData.refreshToken, appData.accessToken);
      if (result === 204) {
        if (refreshTimerId?.timerId) clearInterval(refreshTimerId.timerId);
        setRefreshTimerId({ timerId: null });
        setAppData({ count: 0, refreshToken: "", accessToken: "" });
        SessionManager.clearSession();
        history.push("/login");
      } else {
        showToast("Logout failed.", "error");
      }
    } catch (err) {
      ErrorLoggingService.error("Logout error", err);
      showToast("Logout error.", "error");
    }
  };

  // ── Render: top bar ─────────────────────────────────────────────
  const hospital = userInfo?.attributes?.hospital?.[0] || "—";
  const siteId = userData?.activeSiteId || "—";

  const topbar = (
    <div className="clinician-topbar">
      <IonImg className="clinician-topbar-logo" src={calyanlogo} alt="Calyan" />
      <div className="clinician-topbar-meta">
        <div>
          <div className="clinician-topbar-meta-label">Hospital</div>
          <div className="clinician-topbar-meta-value">{hospital}</div>
        </div>
        <div>
          <div className="clinician-topbar-meta-label">Site ID</div>
          <div className="clinician-topbar-meta-value">{siteId}</div>
        </div>
      </div>
      <div className="clinician-topbar-actions">
        <button
          className={`clinician-topbar-action ${profileModalOpen ? "is-active" : ""}`}
          onClick={handleProfileClick}
          aria-label="Profile"
        >
          <FiUser /> <span className="clinician-topbar-action-label">Profile</span>
        </button>
        <button
          className={`clinician-topbar-action ${view === VIEW.SETTINGS ? "is-active" : ""}`}
          onClick={handleSettingsClick}
          aria-label="Settings"
        >
          <FiSettings /> <span className="clinician-topbar-action-label">Settings</span>
        </button>
        <button
          className="clinician-topbar-action"
          onClick={handleLogoutClick}
          aria-label="Logout"
        >
          <FiLogOut /> <span className="clinician-topbar-action-label">Logout</span>
        </button>
      </div>
    </div>
  );

  // ── Render: patient card ─────────────────────────────────────────
  const renderCard = (device) => {
    const subjectId = device?.attributes?.subjectId || "—";
    const daysToRecharge = extractDaysToRecharge(device);
    const impedance = extractImpedance(device);
    const lastSyncIso = device?._modified;
    const daysSinceSync = lastSyncIso ? daysAgo(lastSyncIso) : null;

    const rechargeFlagged = daysToRecharge != null && daysToRecharge <= RECHARGE_SOON_DAYS;
    const syncFlagged = daysSinceSync == null || daysSinceSync > STALE_DAYS;
    const impedanceFlagged = impedance != null && (impedance < IMPEDANCE_MIN || impedance > IMPEDANCE_MAX);

    return (
      <div
        key={device?.thingId || subjectId}
        className={`patient-card ${selectedPatientId === subjectId ? "is-selected" : ""}`}
        onClick={() => handleSelectPatient(subjectId)}
        role="button"
        tabIndex={0}
      >
        <div className="patient-card-info">
          <div className="patient-card-id">ID: {subjectId}</div>

          {/* Metrics — two columns: label left, value right. Keeps the
              card narrow so the report pane gets the rest of the screen. */}
          <dl className="patient-card-device">
            <div className={`device-field ${rechargeFlagged ? "is-flagged" : ""}`}>
              <dt className="device-field-label">Days to Recharge</dt>
              <dd className="device-field-value">
                {daysToRecharge != null ? daysToRecharge : "—"}
              </dd>
            </div>
            <div className={`device-field ${syncFlagged ? "is-flagged" : ""}`}>
              <dt className="device-field-label">Days Since Sync</dt>
              <dd className="device-field-value">
                {daysSinceSync != null ? daysSinceSync : "—"}
              </dd>
            </div>
            <div className={`device-field ${impedanceFlagged ? "is-flagged" : ""}`}>
              <dt className="device-field-label">Impedance</dt>
              <dd className="device-field-value">
                {impedance != null ? `${impedance} Ω` : "—"}
              </dd>
            </div>
          </dl>
        </div>

        {/* Action column — report / symptoms / messages, uniform tiles
            stacked vertically so the card stays narrow. */}
        <div className="patient-card-tiles">
          <div className="notif-tile-wrapper">
            <div
              className={`notif-tile ${view === VIEW.REPORT && selectedPatientId === subjectId ? "is-active" : ""}`}
              onClick={(e) => handleReportClick(subjectId, e)}
              role="button"
              tabIndex={0}
              aria-label="Generate report"
              title="Report"
            >
              <FiFileText className="notif-tile-icon" />
            </div>
            <span className="notif-tile-label">Report</span>
          </div>

          <div className="notif-tile-wrapper">
            <div
              className={`notif-tile ${view === VIEW.SYMPTOMS && selectedPatientId === subjectId ? "is-active" : ""}`}
              onClick={(e) => handleSymptomsClick(subjectId, e)}
              role="button"
              tabIndex={0}
              aria-label="View symptoms"
              title="Symptoms"
            >
              <IoMdPerson className="notif-tile-icon" />
            </div>
            <span className="notif-tile-label">Symptoms</span>
          </div>

          <div className="notif-tile-wrapper">
            <div
              className={`notif-tile ${view === VIEW.MESSAGES && selectedPatientId === subjectId ? "is-active" : ""}`}
              onClick={(e) => handleMessagesClick(subjectId, e)}
              role="button"
              tabIndex={0}
              aria-label="View messages"
              title="Messages"
            >
              <FiMessageSquare className="notif-tile-icon" />
            </div>
            <span className="notif-tile-label">Messages</span>
          </div>
        </div>
      </div>
    );
  };

  // ── Render: right pane ───────────────────────────────────────────
  // Right pane is patient-context only. Profile/Settings render outside
  // this function (modal + full-screen takeover respectively).
  const renderRightPane = () => {
    if (!selectedPatient || view === VIEW.NONE) {
      return (
        <div className="clinician-placeholder">
          <FiActivity className="clinician-placeholder-icon" />
          <div className="clinician-placeholder-title">No view selected</div>
          <div className="clinician-placeholder-text">
            Pick a patient and click the <strong>Report</strong>, <strong>Symptoms</strong>,
            or <strong>Messages</strong> tile to see details here.
          </div>
        </div>
      );
    }

    const sid = selectedPatient?.attributes?.subjectId;
    const backButton = (
      <button
        className="clinician-back-mobile"
        onClick={() => setView(VIEW.NONE)}
        aria-label="Back to patient list"
      >
        ← Back
      </button>
    );
    if (view === VIEW.SYMPTOMS) {
      return (
        <>
          <div className="clinician-right-header">
            {backButton}
            <div className="clinician-right-title">Symptoms</div>
            <div className="clinician-right-subtitle">Patient {sid}</div>
          </div>
          <div className="clinician-right-body">
            {rightPaneLoading ? (
              <div className="clinician-loading">Loading symptoms…</div>
            ) : symptomList.length === 0 ? (
              <div className="clinician-placeholder">
                <div className="clinician-placeholder-title">No symptoms reported</div>
              </div>
            ) : (
              <div className="event-list">
                {symptomList.map((s, i) => (
                  <div className="event-item" key={i}>
                    <div className="event-item-meta">
                      {s.name ? <strong style={{ color: "var(--neutral-900)" }}>{s.name}</strong> : null}
                      {s.name && s.ts ? " • " : null}
                      {s.ts ? formatAppDateTime(s.ts, { time: "h:mm A", separator: " • " }) : null}
                    </div>
                    {s.body && <div className="event-item-body">{s.body}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      );
    }

    if (view === VIEW.MESSAGES) {
      return (
        <>
          <div className="clinician-right-header">
            {backButton}
            <div className="clinician-right-title">Messages</div>
            <div className="clinician-right-subtitle">Patient {sid}</div>
          </div>
          <div className="clinician-right-body">
            {rightPaneLoading ? (
              <div className="clinician-loading">Loading messages…</div>
            ) : messageList.length === 0 ? (
              <div className="clinician-placeholder">
                <div className="clinician-placeholder-title">No messages</div>
              </div>
            ) : (
              <div className="event-list">
                {messageList.map((m, i) => (
                  <div className="event-item" key={i}>
                    <div className="event-item-meta">
                      {m.direction === "in" ? "← Patient" : "→ Clinician"}
                      {" • "}
                      {m.ts ? formatAppDateTime(m.ts, { time: "h:mm A", separator: " • " }) : "—"}
                    </div>
                    <div className="event-item-body">{m.body}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      );
    }

    if (view === VIEW.REPORT) {
      const rangeText = reportRange
        ? `${reportRange.from} → ${reportRange.to}`
        : "";
      return (
        <>
          <div className="clinician-right-header">
            {backButton}
            <div className="clinician-right-title">Report</div>
            <div className="clinician-right-subtitle">
              Patient {reportForSubjectId || sid}
              {rangeText && <> · {rangeText}</>}
            </div>
          </div>
          <div className="clinician-right-body clinician-report-body">
            {reportPhase === "loading" && (
              <div className="clinician-report-loading">
                <IonSpinner name="crescent" />
                <div className="clinician-report-loading-title">Generating report from data sync logs…</div>
                <div className="clinician-report-loading-hint">
                  {reportProgress || "Fetching data sync logs from the device for the selected date range."}
                </div>
              </div>
            )}
            {reportPhase === "generating" && (
              <div className="clinician-report-loading">
                <IonSpinner name="crescent" />
                <div className="clinician-report-loading-title">Building report PDF…</div>
                <div className="clinician-report-loading-hint">
                  Compiled {reportRevisionsCount} data sync log{reportRevisionsCount === 1 ? "" : "s"} for the selected date range.
                </div>
              </div>
            )}
            {reportPhase === "error" && (
              <div className="clinician-placeholder">
                <div className="clinician-placeholder-title">Report failed</div>
                <div className="clinician-placeholder-text">{reportError || "Unknown error."}</div>
              </div>
            )}
            {reportPhase === "ready" && reportNotice && (
              <div className="clinician-report-notice" role="status">{reportNotice}</div>
            )}
            {reportPhase === "ready" && reportBlobUrl && (
              // `pagemode=none` collapses the viewer's page-thumbnail rail,
              // which ate a third of the pane to show two postage stamps.
              <iframe
                className="clinician-report-frame"
                title={`Report for ${reportForSubjectId || sid}`}
                src={`${reportBlobUrl}#pagemode=none&navpanes=0&view=FitH`}
              />
            )}
          </div>
        </>
      );
    }

    return null;
  };

  // ── Settings full-screen ─────────────────────────────────────────
  const renderSettingsFullScreen = () => (
    <div className="clinician-settings-full">
      <div className="clinician-settings-full-header">
        <button className="clinician-settings-back" onClick={handleSettingsBack}>
          ← Back to Dashboard
        </button>
        <div className="clinician-settings-title">Settings</div>
        <div /> {/* spacer to keep title centered */}
      </div>
      <div className="clinician-settings-full-body">
        <Settings showTitle={false} />
      </div>
    </div>
  );

  // ── Profile modal ────────────────────────────────────────────────
  const renderProfileModal = () => {
    if (!profileModalOpen) return null;
    return (
      <div className="profile-modal-overlay" onClick={() => setProfileModalOpen(false)}>
        <div className="profile-modal" onClick={(e) => e.stopPropagation()}>
          <div className="profile-modal-header">
            <div className="profile-modal-title">Profile</div>
            <button className="profile-modal-close" onClick={() => setProfileModalOpen(false)}>×</button>
          </div>
          <div className="profile-modal-body">
            <Profile />
          </div>
        </div>
      </div>
    );
  };

  // ── Patient workspace (the normal dashboard area) ────────────────
  const renderPatientWorkspace = () => (
    <div className="clinician-main">
      <div className="clinician-left-pane">
        <div className="clinician-toolbar">
          <div className="clinician-search">
            <FiSearch className="clinician-search-icon" />
            <input
              className="clinician-search-input"
              placeholder="Search by Patient ID"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <select
            className="clinician-sort-select"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            aria-label="Sort patients by"
          >
            {SORT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        <div className="clinician-patient-list">
          {isLoading ? (
            <div className="clinician-loading">Loading patients…</div>
          ) : sortedPatients.length === 0 ? (
            <div className="clinician-empty">
              {searchQuery ? "No patients match your search." : "No patients found for this site."}
            </div>
          ) : (
            sortedPatients.map(renderCard)
          )}
        </div>
      </div>

      <div className="clinician-right-pane">
        {renderRightPane()}
      </div>
    </div>
  );

  // ── Final render ────────────────────────────────────────────────
  // is-right-pane-active drives the mobile drill-down behaviour:
  // on phones, the patient list is hidden when a content view is open
  // (and vice-versa). Desktop ignores this class.
  const rightPaneActive = view === VIEW.SYMPTOMS || view === VIEW.MESSAGES || view === VIEW.REPORT;
  return (
    <div className={`clinician-app ${rightPaneActive ? "is-right-pane-active" : ""}`}>
      {topbar}
      {view === VIEW.SETTINGS ? renderSettingsFullScreen() : renderPatientWorkspace()}
      {renderProfileModal()}
      {toast && (
        <div className={`clinician-toast is-${toast.kind}`}>{toast.msg}</div>
      )}
    </div>
  );
};

export default ClinicianDashboard;
